import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, LocateFixed, Loader2, Pencil, MapPin, X } from "lucide-react";

import { TILE_URL, TILE_ATTR } from "@/lib/mapTiles";

const TILE = TILE_URL;
const SHARJAH = [25.3463, 55.4209];

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:#1E3A8A;border:3px solid #fff;
    box-shadow:0 3px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;
    font-weight:900;font-size:15px">&#9678;</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

function halfDiagonalKm(b) {
  const R = 6371;
  const dLat = ((b.north - b.south) * Math.PI) / 180;
  const dLng = ((b.east - b.west) * Math.PI) / 180;
  const latKm = dLat * R;
  const lngKm = dLng * R * Math.cos((((b.north + b.south) / 2) * Math.PI) / 180);
  return Math.sqrt(latKm * latKm + lngKm * lngKm) / 2;
}

export default function AreaMapPicker({ onGeometry, pastAreas = [], heightClass = "h-[58vh] min-h-[340px]" }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const drawnRef = useRef(null);
  const pastRef = useRef(null);
  const drawCtlRef = useRef(null);
  const emitRef = useRef(onGeometry);
  emitRef.current = onGeometry;

  const [mode, setMode] = useState("pin");
  const [radiusKm, setRadiusKm] = useState(3);
  const [center, setCenter] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const skipSuggest = useRef(false);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [label, setLabel] = useState("");

  const emitPin = useCallback((latlng, rKm) => {
    emitRef.current({
      type: "Point",
      coordinates: [latlng.lng, latlng.lat],
      radiusKm: Math.round(rKm * 100) / 100,
    });
  }, []);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: true, zoomAnimation: false }).setView(SHARJAH, 12);
    L.tileLayer(TILE, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);

    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    map.on("click", (e) => {
      if (mapRef.current?._jamMode !== "pin") return;
      setCenter(e.latlng);
    });

    mapRef.current = map;
    mapRef.current._jamMode = "pin";
    const t = setTimeout(() => { if (mapRef.current) map.invalidateSize(); }, 250);
    return () => { clearTimeout(t); map.stop(); map.off(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => { if (mapRef.current) mapRef.current._jamMode = mode; }, [mode]);

  // draw already-scanned areas (translucent grey) so the manager avoids paying twice
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pastRef.current) { pastRef.current = new L.FeatureGroup(); map.addLayer(pastRef.current); }
    const layer = pastRef.current;
    layer.clearLayers();
    (pastAreas || []).forEach((a) => {
      const g = a.geojson;
      if (!g) return;
      if (g.type === "Point" && g.coordinates) {
        L.circle([g.coordinates[1], g.coordinates[0]], {
          radius: (g.radiusKm || 0) * 1000, color: "#9CA3AF", weight: 1.5,
          dashArray: "5,5", fillColor: "#9CA3AF", fillOpacity: 0.1, interactive: false,
        }).addTo(layer);
      } else if (g.type === "Polygon" && g.coordinates) {
        L.polygon(g.coordinates[0].map((c) => [c[1], c[0]]), {
          color: "#9CA3AF", weight: 1.5, dashArray: "5,5", fillColor: "#9CA3AF", fillOpacity: 0.1, interactive: false,
        }).addTo(layer);
      }
    });
  }, [pastAreas]);

  // pin + circle rendering
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mode !== "pin" || !center) {
      if (markerRef.current) { map.removeLayer(markerRef.current); markerRef.current = null; }
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
      return;
    }
    if (!markerRef.current) {
      markerRef.current = L.marker(center, { icon: pinIcon, draggable: true }).addTo(map);
      markerRef.current.on("drag", (e) => {
        const ll = e.target.getLatLng();
        circleRef.current?.setLatLng(ll);
      });
      markerRef.current.on("dragend", (e) => setCenter(e.target.getLatLng()));
    } else {
      markerRef.current.setLatLng(center);
    }
    if (!circleRef.current) {
      circleRef.current = L.circle(center, {
        radius: radiusKm * 1000, color: "#1E3A8A", weight: 2,
        fillColor: "#1E3A8A", fillOpacity: 0.12,
      }).addTo(map);
    } else {
      circleRef.current.setLatLng(center);
      circleRef.current.setRadius(radiusKm * 1000);
    }
    emitPin(center, radiusKm);
  }, [center, radiusKm, mode, emitPin]);

  // draw mode controls
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mode === "draw" && !drawCtlRef.current) {
      const ctl = new L.Control.Draw({
        position: "topright",
        draw: {
          polygon: { shapeOptions: { color: "#1E3A8A" } },
          rectangle: { shapeOptions: { color: "#1E3A8A" } },
          circle: { shapeOptions: { color: "#1E3A8A" } },
          polyline: false, marker: false, circlemarker: false,
        },
        edit: { featureGroup: drawnRef.current, remove: true },
      });
      map.addControl(ctl);
      drawCtlRef.current = ctl;

      const emit = (layer) => {
        if (layer instanceof L.Circle) {
          const c = layer.getLatLng();
          emitPin(c, layer.getRadius() / 1000);
        } else {
          emitRef.current(layer.toGeoJSON().geometry);
        }
      };
      map.on(L.Draw.Event.CREATED, (e) => {
        drawnRef.current.clearLayers();
        drawnRef.current.addLayer(e.layer);
        emit(e.layer);
      });
      map.on(L.Draw.Event.EDITED, (e) => e.layers.eachLayer(emit));
      map.on(L.Draw.Event.DELETED, () => emitRef.current(null));
    }
    if (mode !== "draw" && drawCtlRef.current) {
      map.removeControl(drawCtlRef.current);
      drawCtlRef.current = null;
      drawnRef.current?.clearLayers();
    }
  }, [mode, emitPin]);

  const runSearch = async (text) => {
    if (!text || text.trim().length < 2) return;
    setSearching(true);
    setSuggestions([]);
    try {
      const { data } = await api.get("/places/geosearch", { params: { q: text.trim() } });
      setResults(data);
      if (data.length === 0) toast.error("No place found — try 'Al Quoz Dubai'");
      else pick(data[0]);
    } catch { toast.error("Search unavailable right now"); }
    finally { setSearching(false); }
  };

  const doSearch = (e) => {
    e?.preventDefault();
    runSearch(q);
  };

  // debounced type-ahead
  useEffect(() => {
    if (skipSuggest.current) { skipSuggest.current = false; setSuggestions([]); return; }
    if (q.trim().length < 3) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: q.trim() } });
        setSuggestions(data.slice(0, 5));
      } catch { setSuggestions([]); }
    }, 500);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (r) => {
    const map = mapRef.current;
    if (!map || r.latitude == null) return;
    setMode("pin");
    setResults([]);
    setSuggestions([]);
    setLabel(r.name);
    const ll = L.latLng(r.latitude, r.longitude);
    if (r.bounds) {
      map.fitBounds([[r.bounds.south, r.bounds.west], [r.bounds.north, r.bounds.east]], { padding: [20, 20] });
      setRadiusKm(Math.max(1, Math.min(15, Math.round(halfDiagonalKm(r.bounds) * 10) / 10)));
    } else {
      map.setView(ll, 14);
    }
    setCenter(ll);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not available"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = L.latLng(pos.coords.latitude, pos.coords.longitude);
        setMode("pin");
        setLabel("Around me");
        mapRef.current?.setView(ll, 14);
        setCenter(ll);
        setLocating(false);
      },
      () => { setLocating(false); toast.error("Could not get your location"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-3">
      <form onSubmit={doSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a place, e.g. Al Quoz Dubai"
            data-testid="area-search-input"
            className="w-full h-12 pl-9 pr-9 rounded-lg border border-[#D1D5DB] text-base focus:outline-none focus:border-[#1E3A8A]"
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setResults([]); setSuggestions([]); }} data-testid="area-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#9CA3AF]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button type="submit" disabled={searching} data-testid="area-search-btn"
          className="h-12 px-4 rounded-lg bg-[#1E3A8A] text-white font-bold flex items-center gap-2 disabled:opacity-60">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="hidden sm:inline">Go</span>
        </button>
        <button type="button" onClick={useMyLocation} disabled={locating} data-testid="use-my-location"
          className="h-12 w-12 shrink-0 rounded-lg border-2 border-[#1E3A8A] text-[#1E3A8A] flex items-center justify-center">
          {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <LocateFixed className="w-5 h-5" />}
        </button>
      </form>

      {suggestions.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg divide-y" data-testid="area-suggestions">
          {suggestions.map((s) => (
            <button key={s.place_id} onClick={() => { skipSuggest.current = true; setQ(s.text || s.main_text); runSearch(s.text || s.main_text); }}
              data-testid={`area-suggestion-${s.place_id}`}
              className="w-full text-left px-3 py-3 min-h-[48px] hover:bg-[#F9FAFB] flex items-start gap-2">
              <Search className="w-4 h-4 mt-0.5 text-[#9CA3AF] shrink-0" />
              <span>
                <span className="block font-bold text-sm text-[#111827]">{s.main_text || s.text}</span>
                {s.secondary_text && <span className="block text-xs text-[#6B7280]">{s.secondary_text}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {results.length > 1 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg divide-y" data-testid="area-search-results">
          {results.map((r) => (
            <button key={r.place_id} onClick={() => pick(r)} data-testid={`area-result-${r.place_id}`}
              className="w-full text-left px-3 py-3 hover:bg-[#F9FAFB] flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#1E3A8A] shrink-0" />
              <span>
                <span className="block font-bold text-sm text-[#111827]">{r.name}</span>
                <span className="block text-xs text-[#6B7280]">{r.address}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <div ref={mapEl} data-testid="area-map" className={`w-full ${heightClass} rounded-xl border border-[#E5E7EB] z-0`} />
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[400] flex bg-white rounded-full shadow-md border border-[#E5E7EB] overflow-hidden">
          <button onClick={() => setMode("pin")} data-testid="mode-pin"
            className={`px-4 h-10 text-xs font-bold flex items-center gap-1.5 whitespace-nowrap ${mode === "pin" ? "bg-[#1E3A8A] text-white" : "text-[#374151]"}`}>
            <MapPin className="w-4 h-4" /> Pin<span className="hidden sm:inline"> + radius</span>
          </button>
          <button onClick={() => setMode("draw")} data-testid="mode-draw"
            className={`px-4 h-10 text-xs font-bold flex items-center gap-1.5 whitespace-nowrap ${mode === "draw" ? "bg-[#1E3A8A] text-white" : "text-[#374151]"}`}>
            <Pencil className="w-4 h-4" /> Draw<span className="hidden sm:inline"> shape</span>
          </button>
        </div>
      </div>

      {mode === "pin" ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">
              {center ? (label || "Search radius") : "Tap the map to drop a pin"}
            </span>
            <span className="text-sm font-black text-[#1E3A8A]" data-testid="radius-value">{radiusKm} km</span>
          </div>
          <input type="range" min="0.5" max="20" step="0.5" value={radiusKm}
            onChange={(e) => setRadiusKm(Number(e.target.value))}
            data-testid="area-radius-slider"
            className="w-full h-8 accent-[#1E3A8A]" />
          <p className="text-xs text-[#9CA3AF] mt-1">Drag the pin to move the area. Bigger radius = more places = higher cost.</p>
        </div>
      ) : (
        <p className="text-xs text-[#6B7280]">Use the polygon, rectangle or circle tool (top-right of the map) to draw the exact area.</p>
      )}
    </div>
  );
}
